import Embed from '@public/svgs/builder/assignment/embed.svg';
import Files from '@public/svgs/builder/assignment/files-drag.svg';
import Guide from '@public/svgs/builder/assignment/guide.svg';
import Image from '@public/svgs/builder/assignment/image.svg';
import Media from '@public/svgs/builder/assignment/media.svg';
import TextSvg from '@public/svgs/builder/assignment/text.svg';
import Url from '@public/svgs/builder/assignment/url.svg';
import DragNDrop from '@public/svgs/builder/quiz/drag-and-drop.svg';
import DragNDropMini from '@public/svgs/builder/quiz/drag-and-drop-mini.svg';
import FillNBlank from '@public/svgs/builder/quiz/fill-in-blank.svg';
import FillNBlankMini from '@public/svgs/builder/quiz/fill-in-blank-mini.svg';
import Matching from '@public/svgs/builder/quiz/matching.svg';
import MatchingMini from '@public/svgs/builder/quiz/matching-mini.svg';
import MultiChoice from '@public/svgs/builder/quiz/multiple-choice.svg';
import MultiChoiceMini from '@public/svgs/builder/quiz/multiple-choice-mini.svg';
import SequencingQuestion from '@public/svgs/builder/quiz/sequencing.svg';
import SequencingQuestionMini from '@public/svgs/builder/quiz/sequencing-mini.svg';
import Paragraph from '@public/svgs/builder/quiz/paragraph.svg';
import EssayMini from '@public/svgs/builder/quiz/essay-mini.svg';
import SectionBlank from '@public/svgs/builder/quiz/section-blank.svg';
import SectionBreakMini from '@public/svgs/builder/quiz/section-break-mini.svg';
import ShortAnswer from '@public/svgs/builder/quiz/short-answer.svg';
import ShortAnswerMini from '@public/svgs/builder/quiz/short-answer-mini.svg';
import TrueOrFalse from '@public/svgs/builder/quiz/true-or-false.svg';
import TrueOrFalseMini from '@public/svgs/builder/quiz/true-or-false-mini.svg';
import {
    Activity,
    ActivityStatusOverride,
    Assignment,
    ContentBlock,
    GradingScheme,
    MultiSubmission,
    Quiz,
    QuizPageBreaks,
    QuizQuestionKind,
    QuizQuestionNumbering,
    SingleSubmission,
    StudentSubmission,
    SubmissionKind,
    SubmissionStatus,
} from '@graphql/generated-graphql';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import { useRef, useEffect } from 'react';
import { useFormContext, useWatch, useFormState } from 'react-hook-form';
import { statusToCss, statusToIcon } from '@hooks/grading/useGradeSubmissions';
import { GradeScheme, Text } from '@skipper/ui';
import { Button, MenuContainer, MenuItemContainer } from '@skipper/ui';
import { Menu } from '@headlessui/react';

export const AssignmentImageArray = [
    { label: 'Text', img: TextSvg, blockType: ContentBlock.Text },
    { label: 'Image', img: Image, blockType: ContentBlock.Image },
    { label: 'Media', img: Media, blockType: 'media' },
    { label: 'Embed', img: Embed, blockType: ContentBlock.Embed },
    { label: 'URL\nwebsite', img: Url, blockType: ContentBlock.Link },
    { label: 'File\ndownload', img: Files, blockType: ContentBlock.File },
    { label: 'Instructor\nguide', img: Guide, blockType: ContentBlock.Guide },
];

export const QuizImageArray = [
    {
        label: 'Multiple\nchoice',
        img: MultiChoice,
        imgMini: MultiChoiceMini,
        blockType: QuizQuestionKind.Choice,
    },
    {
        label: 'Drag &\ndrop',
        img: DragNDrop,
        imgMini: DragNDropMini,
        blockType: QuizQuestionKind.Drag,
    },
    {
        label: 'True or\nFalse',
        img: TrueOrFalse,
        imgMini: TrueOrFalseMini,
        blockType: QuizQuestionKind.Tf,
    },
    {
        label: 'Matching',
        img: Matching,
        imgMini: MatchingMini,
        blockType: QuizQuestionKind.Matching,
    },
    {
        label: 'Sequence',
        img: SequencingQuestion,
        imgMini: SequencingQuestionMini,
        blockType: QuizQuestionKind.Sequence,
    },
    {
        label: 'Fill in the\nblank',
        img: FillNBlank,
        imgMini: FillNBlankMini,
        blockType: QuizQuestionKind.Fill,
    },
    {
        label: 'Short\nanswer',
        img: ShortAnswer,
        imgMini: ShortAnswerMini,
        blockType: QuizQuestionKind.Short,
    },
    { label: 'Essay', img: Paragraph, imgMini: EssayMini, blockType: QuizQuestionKind.Essay },
    {
        label: 'Section\nbreak',
        img: SectionBlank,
        imgMini: SectionBreakMini,
        blockType: QuizQuestionKind.Section,
    },
];

export const PagebreakOptions = [
    {
        id: '',
        value: 'None',
    },
    {
        id: QuizPageBreaks.Section,
        value: 'After section break',
    },
];

export const SubmissionOptions = [
    {
        id: SubmissionKind.Online,
        value: 'Online Submission',
    },
    {
        id: SubmissionKind.OnPaper,
        value: 'On Paper',
    },
    {
        id: SubmissionKind.NoSubmission,
        value: 'No Submission (read, watch...)',
    },

    {
        id: SubmissionKind.InPerson,
        value: 'In Person Activity',
    },
];

export const AttemptOptions = [
    {
        id: 1,
        value: '1 time',
    },
    {
        id: 2,
        value: '2 times',
    },
    {
        id: 3,
        value: '3 times',
    },
    {
        id: 4,
        value: '4 times',
    },
    {
        id: 5,
        value: '5 times',
    },
    {
        id: 10,
        value: '10 times',
    },
    {
        id: -1,
        value: 'Unlimited',
    },
];

export const TimeLimitOptions = [1, 2, 3, 4, 5, 10, 15, 20, 30, 45, 60, 90, 120].map((n) => {
    return {
        id: n,
        value: `${n} minute${n === 1 ? '' : 's'}`,
    };
});

TimeLimitOptions.push({
    id: -1,
    value: 'None',
});

export const QuizQuestionNumberingOptions = [
    {
        id: QuizQuestionNumbering.Auto,
        value: 'Auto Number',
    },
    {
        id: QuizQuestionNumbering.AutoSection,
        value: 'Auto Number per Section',
    },
    {
        id: QuizQuestionNumbering.Manual,
        value: 'Manually Entered',
    },
    {
        id: QuizQuestionNumbering.None,
        value: 'No Numbering',
    },
];

export const PublishOptions = [
    {
        value: `draft`,
        label: 'Draft',
    },
    {
        value: `published`,
        label: 'Published',
    },
];

export const VisibleOptions = [
    {
        value: 'visible',
        label: 'Visible',
    },
    {
        value: 'hidden',
        label: 'Hidden',
    },
];

export type CurrentActivity = Activity & { item: Assignment };
export type CurrentQuiz = Activity & { item: Quiz };

export type ActivityElement = CurrentActivity | CurrentQuiz;

export function isQuizType(val: CurrentActivity | CurrentQuiz): val is CurrentQuiz {
    return val?.item?.type === 'quiz';
}

export function formatDayjs(val?: string) {
    if (val) {
        const day = dayjs(val).local();
        if (day.isValid()) {
            return day;
        }
    }
    return undefined;
}

export type ErrorObject = {
    [field: string]: string[];
};

export function MapToForm(errors: any) {
    const object: ErrorObject = {};

    errors?.inner?.forEach((x: any) => {
        if (x.path !== undefined) {
            object[x.path] = x.errors;
        }
    });

    return object;
}

const UPDATE_AUTO_INTERVAL = 6000;
/**
 * Nothing is specific to the page therefore this should work for both Assignments and Quizzess
 *
 * @param param0
 * @returns
 */
export function AutoSubmitHandler({
    onSuccess,
    onError,
    isDeleted,
}: {
    onSuccess: (d: any) => void;
    onError: (d: any) => void;
    isDeleted: React.MutableRefObject<boolean>;
}) {
    const { handleSubmit, control } = useFormContext();
    const router = useRouter();
    const allInputs = useWatch({});

    const { isDirty, isValid } = useFormState({
        control,
    });
    const updating = useRef<NodeJS.Timer | null>(null);

    useEffect(() => {
        const confirmationMessage =
            'You have unsaved information on this page. Leave page without saving?';

        /**
         * User tries to route to new URL and/or tries to reload the current
         *
         * @param e
         * @returns
         */
        const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
            (e || window.event).returnValue = confirmationMessage;
            return confirmationMessage; // Gecko + Webkit, Safari, Chrome etc.
        };
        /**
         * Handles Client Side routing and prevents if theres something to save thats not valid/pending
         *
         * @param url
         */
        const beforeRouteHandler = (url: string) => {
            if (router.pathname !== url && !confirm(confirmationMessage)) {
                // to inform NProgress or something ...
                router.events.emit('routeChangeError');
                // tslint:disable-next-line: no-string-throw
                throw `Route change to "${url}" was aborted (this error can be safely ignored). See https://github.com/zeit/next.js/issues/2476.`;
            }
        };
        const submission = () => {
            if (updating.current) clearInterval(updating.current);
            updating.current = null;
            if (isDeleted.current) return;
            handleSubmit(onSuccess, onError)?.();
        };

        if (!isValid) {
            router.events.off('routeChangeStart', submission);
            router.events.on('routeChangeStart', beforeRouteHandler);
            window.addEventListener('beforeunload', beforeUnloadHandler);
        } else {
            window.removeEventListener('beforeunload', beforeUnloadHandler);
            router.events.off('routeChangeStart', beforeRouteHandler);
            router.events.on('routeChangeStart', submission);
        }

        const finalLastSave = () => {
            if (updating.current) clearInterval(updating.current);
            updating.current = null;

            if (isValid && isDirty) {
                submission?.();
            }
        };

        window.addEventListener('beforeunload', finalLastSave);

        if (isValid && isDirty) {
            updating.current = setTimeout(() => {
                submission?.();
                updating.current = null;
            }, UPDATE_AUTO_INTERVAL);
        }

        return () => {
            if (!!updating.current) {
                clearInterval(updating.current);
                updating.current = null;
            }
            router.events.off('routeChangeStart', beforeRouteHandler);
            router.events.off('routeChangeStart', submission);

            window.removeEventListener('beforeunload', beforeUnloadHandler);
            window.removeEventListener('beforeunload', finalLastSave);
        };
    }, [isDirty, isValid, onSuccess, onError, handleSubmit, allInputs]);

    return null;
}

export function getSubmissionCount({
    submissions,
}: {
    submissions: (SingleSubmission | MultiSubmission)[];
}) {
    if (submissions.length === 0) {
        return 0;
    }

    return (
        submissions
            ?.map((item) =>
                item?.__typename === 'MultiSubmission' ? item?.submissions : item?.submission,
            )
            ?.filter((t) => !!t)
            .flat()?.length ?? 0
    );
}

export function getStudentViewSubmissionCount({
    submissions,
}: {
    submissions: (SingleSubmission | MultiSubmission)[];
}) {
    if (submissions.length === 0) {
        return 0;
    }

    if (submissions[0]?.__typename === 'MultiSubmission')
        return submissions[0]?.submissions?.filter((item) => !item?.isDraft)?.length ?? 0;
    else if (submissions[0]?.__typename === 'SingleSubmission')
        return submissions[0].submission?.attachments?.length || 0;
    else return 0;
}

export function getSubmissionInference({
    submissions,
}: {
    submissions: (SingleSubmission | MultiSubmission)[];
}): (SingleSubmission | MultiSubmission)[] {
    if (submissions?.[0]?.__typename === 'MultiSubmission') {
        return submissions as MultiSubmission[];
    }
    return submissions as SingleSubmission[];
}

export function StatusItem({
    status,
    grade,
    gradedAt,
    maxPoints,
    gradingScheme,
    isGraded,
    gradePercentage,
    isQuizGraded = true,
    isAssignmentGraded = true,
    isQuiz,
    showNeedsGradingAssignment = true,
    assignmentShowPending,
}: {
    status?: StudentSubmission['status'];
    grade?: number;
    gradedAt?: boolean;
    maxPoints?: number;
    gradingScheme?: GradingScheme;
    isGraded?: boolean;
    gradePercentage?: number;
    isQuizGraded?: boolean;
    isAssignmentGraded?: boolean;
    isQuiz?: boolean;
    showNeedsGradingAssignment?: boolean;
    assignmentShowPending?: boolean;
}) {
    if (
        typeof grade === 'number' &&
        grade >= 0 &&
        !!maxPoints &&
        (!status || status === SubmissionStatus.Completed) &&
        isQuizGraded
    )
        return (
            <GradeScheme
                size="small"
                css={{ px: 8, py: 4, height: '28px' }}
                gradingScheme={gradingScheme as any}
                maxPoints={maxPoints}
                grade={grade}
                isGraded={isGraded}
                gradePercentage={gradePercentage}
            />
        );

    const cssStyle = statusToCss(status);
    const Icon = statusToIcon(status);

    if (
        status === SubmissionStatus.MarkedDone &&
        (!isAssignmentGraded || !showNeedsGradingAssignment) &&
        !assignmentShowPending
    )
        return;

    if (!isQuizGraded && status === SubmissionStatus.Completed && !gradedAt)
        return <Text css={statusToCss('needs_grading')}>Needs review</Text>;
    if (status === null || status === SubmissionStatus.Completed) return null;

    if (
        isAssignmentGraded &&
        showNeedsGradingAssignment &&
        (status === SubmissionStatus.MarkedDone || status === SubmissionStatus.NeedsGrading)
    )
        return <Text css={statusToCss('needs_grading')}>Needs grading</Text>;

    if (assignmentShowPending || (isQuiz && status === SubmissionStatus.NeedsGrading))
        return <Text css={statusToCss('needs_grading')}>Pending</Text>;

    switch (status) {
        case SubmissionStatus.NeedsGrading:
            return;
        default:
            return (
                <Text css={cssStyle}>
                    {Icon && (
                        <span>
                            <Icon />
                        </span>
                    )}
                    {status}
                </Text>
            );
    }
}

export function ActivityStatusOverrideSelector({
    onSelection,
    children,
    ActivityOverrideStatuses,
}: {
    onSelection: (status: ActivityStatusOverride | null) => void;
    children: React.ReactNode;
    ActivityOverrideStatuses: any;
}) {
    return (
        <Menu as={MenuContainer}>
            {({ open }) => {
                return (
                    <>
                        <Menu.Button
                            as={Button}
                            size="small"
                            type="button"
                            variant="textOnly"
                            open={open}
                            css={{
                                ['&:disabled']: {
                                    backgroundColor: 'white',
                                    ['&:hover']: {
                                        backgroundColor: 'white',
                                    },
                                },
                            }}
                        >
                            {children}
                        </Menu.Button>
                        <Menu.Items as={MenuItemContainer} css={{ py: '6px' }} size="small">
                            <Menu.Item>
                                <button
                                    onClick={() => {
                                        onSelection(null);
                                    }}
                                    className="px-2 py-1"
                                >
                                    <div className="w-full h-[28px] border-2 rounded-md border-dashed" />
                                </button>
                            </Menu.Item>
                            {ActivityOverrideStatuses.map((status) => {
                                return (
                                    <Menu.Item key={status}>
                                        <button
                                            onClick={() => {
                                                onSelection(ActivityStatusOverride[status]);
                                            }}
                                            className="px-2 py-1"
                                        >
                                            <StatusItem
                                                key={status}
                                                status={SubmissionStatus[status]}
                                            />
                                        </button>
                                    </Menu.Item>
                                );
                            })}
                        </Menu.Items>
                    </>
                );
            }}
        </Menu>
    );
}

export const DEFAULT_MAX_POINTS = 100;
